import type { MerchantUser } from 'api/merchantUsers'
import { buildTranslate } from 'locales'
import React from 'react'
import EscalationNotificationScopeNote from './EscalationNotificationScopeNote'
import EscalationRationaleList from './EscalationRationaleList'
import NotifyLocationManagersToggle, {
  recipientFieldLabel,
} from './NotifyLocationManagersToggle'
import type { InviteAffordance } from './RecipientPicker'
import RecipientPicker from './RecipientPicker'

const t = buildTranslate('recovery_ai.settings.escalation_contacts')

/**
 * The two picker namespaces a host's single `instanceId` expands into.
 *
 *   context: exported because only the additional picker renders a description
 *   element, so the primary namespace has no DOM footprint to assert against —
 *   and the Settings pane already ships these exact strings at its call sites,
 *   so a derivation that drifted by a character would churn every id.
 */
export const pickerInstanceIds = (instanceId: string) => ({
  primary: `${instanceId}-primary`,
  additional: `${instanceId}-additional`,
})

export type EscalationContactsBodyProps = {
  /** Namespaces the toggle's label id and both pickers' description ids (C1). */
  instanceId: string
  /** Candidate recipients. The HOST fetches; the body never does. */
  users: MerchantUser[]

  /**
   * Controlled selection. `number[]` for the primary too, matching
   * RecipientPicker's `value` in both modes — single mode holds at most one id.
   * `onChange` emits the FULL next id array; mapping it to the mutation's
   * `primaryUserId: number | null` is the controller's job, not the body's.
   */
  primaryIds: number[]
  onPrimaryChange: (ids: number[]) => void
  additionalIds: number[]
  onAdditionalChange: (ids: number[]) => void

  /**
   * escalation_routing_notify_location_managers. The body neither stages nor
   * persists it — the host's controller decides whether the change is staged or
   * eager, which is what lets ONE body serve all three surfaces unforked.
   */
  notifyLocationManagers: boolean
  onNotifyChange: (next: boolean) => void

  /** CASL manage/RecoveryAI. Disables the toggle and both pickers. */
  canManage: boolean

  /**
   * Which of `users` are admins, for both pickers' ADMINS / ALL OTHER USERS split
   * (#3883). Required and injected: the body is fetch-free, and the predicate is
   * RBAC-aware, so it needs a roles query the host's controller already owns.
   */
  isAdmin: (user: MerchantUser) => boolean

  /**
   * An eager notify write is in flight — disables the TOGGLE ONLY, so a
   * double-click can't fire a duplicate PUT. Always false on a staged host.
   */
  isNotifySaving?: boolean

  /**
   * ANY write in flight — disables the toggle AND both pickers. Serialising
   * matters on a replace-not-merge endpoint, where two in-flight PUTs could land
   * out of order.
   */
  isBusy?: boolean

  /**
   * No persisted baseline, so a recipient edit cannot be saved — disables both
   * pickers, leaving the toggle live.
   *
   *   context: don't offer an edit that cannot be persisted. Without this the
   *   pickers render EMPTY (hydration bails with no baseline) but editable, so a
   *   merchant picks someone, the staged host arms Save off the notify toggle
   *   instead, and the recipients write is skipped under a success banner. The
   *   toggle stays live on purpose: it writes to the config, not this resource,
   *   and on the wizard step it is the documented escape from an unreachable
   *   contacts resource.
   */
  recipientsReadOnly?: boolean

  /** Forwarded to the primary picker (the only one that renders it). */
  inviteAffordance?: InviteAffordance
}

/**
 * Chrome-free escalation-contacts editor: the rationale list, the scope note,
 * the notify-location-managers toggle, and the primary + additional recipient
 * pickers. Fully controlled and persistence-agnostic — no title, no gutter, no
 * banners, no Save (the host owns all of those).
 *
 *   context: the BODY calls recipientFieldLabel, not the host, so the toggle and
 *   the primary picker's label can never drift apart across the three surfaces
 *   that render this. The mt-* bands below are documented design rhythm lifted
 *   with the markup.
 */
const EscalationContactsBody = ({
  instanceId,
  users,
  primaryIds,
  onPrimaryChange,
  additionalIds,
  onAdditionalChange,
  notifyLocationManagers,
  onNotifyChange,
  canManage,
  isAdmin,
  isNotifySaving = false,
  isBusy = false,
  recipientsReadOnly = false,
  inviteAffordance,
}: EscalationContactsBodyProps) => {
  const pickerIds = pickerInstanceIds(instanceId)
  // Shared floor: no permission, or a write in flight (serialising matters on a
  // replace-not-merge endpoint).
  const controlsDisabled = !canManage || isBusy
  // The pickers take one more condition than the toggle. recipientsReadOnly is
  // about THIS resource having no baseline; the toggle writes to the config, and
  // on the wizard step it is the documented escape when contacts are unreachable,
  // so disabling it there would close the only way out.
  const pickersDisabled = controlsDisabled || recipientsReadOnly
  const toggleDisabled = controlsDisabled || isNotifySaving

  return (
    <>
      <EscalationRationaleList />

      {/* Own mt-xl-separated band — the design treats the scope note as its
          own band, not part of the routing group below. */}
      <div className="mt-xl">
        <EscalationNotificationScopeNote />
      </div>

      {/* mt-xl (48px) clears the scope band above; the primary picker below
          keeps mt-l (32px) so it still groups with this toggle. */}
      <div className="mt-xl">
        <NotifyLocationManagersToggle
          instanceId={instanceId}
          value={notifyLocationManagers}
          onChange={onNotifyChange}
          disabled={toggleDisabled}
        />
      </div>

      {/* 32px (mt-l): groups with the notify toggle above; the sections below
          use the 48px (mt-xl) gap. */}
      <div className="mt-l">
        <RecipientPicker
          instanceId={pickerIds.primary}
          mode="single"
          users={users}
          isAdmin={isAdmin}
          value={primaryIds}
          onChange={onPrimaryChange}
          label={recipientFieldLabel(notifyLocationManagers)}
          disabled={pickersDisabled}
          inviteAffordance={inviteAffordance}
        />
      </div>

      {/* Figma order: label → description → field. The description is rendered
          inside the picker (via its `description` prop) so it sits under the
          "Additional recipients (optional)" label rather than above it. */}
      <div className="mt-xl">
        <RecipientPicker
          instanceId={pickerIds.additional}
          mode="multi"
          users={users}
          isAdmin={isAdmin}
          value={additionalIds}
          onChange={onAdditionalChange}
          label={t('additional_label')}
          description={t('additional_desc')}
          disabled={pickersDisabled}
        />
      </div>
    </>
  )
}

export default EscalationContactsBody
